/**
 * Widget Dashboard Page
 *
 * Customer dashboard for widget customer portal.
 * Shows welcome message, quick actions, and user info.
 * Note: Header navigation is provided by WidgetLayout
 */

import { Link } from 'react-router-dom';
import { useWidgetAuth } from '../../contexts/WidgetAuthContext';
import { useProviderConfig } from '../../contexts/ProviderConfigContext';
import { Package, Calendar, User, FileText } from 'lucide-react';

export function WidgetDashboard() {
  const { user } = useWidgetAuth();
  const { config } = useProviderConfig();

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {user?.name}!
        </h1>
        <p className="text-gray-600">
          Manage your orders and account with {config?.companyName || 'us'}.
        </p>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* New Order */}
          <Link
            to="../order"
            className="bg-white rounded-lg shadow-sm border-2 border-gray-200 hover:border-primary hover:shadow-md transition-all p-6 group"
          >
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Package className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors">
                  New Order
                </h3>
                <p className="text-xs text-gray-600">Schedule a dumpster</p>
              </div>
            </div>
          </Link>

          {/* Orders */}
          <Link
            to="../orders"
            className="bg-white rounded-lg shadow-sm border-2 border-gray-200 hover:border-primary hover:shadow-md transition-all p-6 group"
          >
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <FileText className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors">
                  My Orders
                </h3>
                <p className="text-xs text-gray-600">View order history</p>
              </div>
            </div>
          </Link>

          {/* Account */}
          <Link
            to="../account"
            className="bg-white rounded-lg shadow-sm border-2 border-gray-200 hover:border-primary hover:shadow-md transition-all p-6 group"
          >
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <User className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors">
                  Account
                </h3>
                <p className="text-xs text-gray-600">Update your profile</p>
              </div>
            </div>
          </Link>

          {/* Schedule */}
          <div className="bg-gray-50 rounded-lg shadow-sm border-2 border-gray-200 p-6 opacity-60 cursor-not-allowed">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-gray-400" />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500">Schedule</h3>
                <p className="text-xs text-gray-400">Coming soon</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Information */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-1">Full Name</dt>
              <dd className="text-sm text-gray-900">{user?.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-1">Email Address</dt>
              <dd className="text-sm text-gray-900">{user?.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-1">Phone Number</dt>
              <dd className="text-sm text-gray-900">{user?.phone}</dd>
            </div>
            {user?.companyName && (
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Company</dt>
                <dd className="text-sm text-gray-900">{user.companyName}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Recent Activity Placeholder */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Orders</h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="max-w-sm mx-auto">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-base font-medium text-gray-900 mb-2">No orders yet</h3>
            <p className="text-sm text-gray-600 mb-6">
              Get started by placing your first order. It only takes a few minutes!
            </p>
            <Link
              to="../order"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 transition-colors"
            >
              Place Your First Order
            </Link>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">Need Help?</h3>
        <p className="text-sm text-blue-800 mb-4">
          Our team is here to assist you with any questions about your orders or services.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href={`tel:${config?.contactPhone}`}
            className="inline-flex items-center justify-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-900 bg-white hover:bg-blue-50 transition-colors"
          >
            Call {config?.contactPhone}
          </a>
          <a
            href={`mailto:${config?.contactEmail}`}
            className="inline-flex items-center justify-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-900 bg-white hover:bg-blue-50 transition-colors"
          >
            Email {config?.contactEmail}
          </a>
        </div>
      </div>
    </div>
  );
}
